import React, { memo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Avatar,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ThumbUp, ThumbDown } from "@mui/icons-material";
import { ProfilePicture } from "../../components/common/profile-picture";
import accepts_remote_icon from "../../img/remote.svg";
import { AvgResponseTimeComponent } from "./avg-response-time-component";
import { Tooltip } from "../../components/common/tooltip/tooltip";

export const TeacherSmallComponent = memo(({
  showSmallTeacherComponent,
  teaching,
  hideLine,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Card
        role="article"
        aria-label={`Teacher profile for ${teaching.teacher.username}`}
        sx={{
          width: { xs: '100%', md: '90%' },
          minWidth: { xs: '100%', md: 500 },
          maxWidth: { xs: '100%', md: 1000 },
          mx: 'auto',
          borderRadius: { xs: 0, md: 2.5 },
          boxShadow: { xs: 'none', md: '0px 2px 7px rgba(0, 0, 0, 0.35)' },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: { xs: 'none', md: 'translateY(-2px)' },
            boxShadow: { xs: 'none', md: '0px 4px 12px rgba(0, 0, 0, 0.25)' },
          },
          '&:focus-within': {
            outline: `2px solid ${muiTheme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: 'row', md: 'row' }}
            spacing={{ xs: 2, md: 3 }}
            alignItems="flex-start"
            sx={{ minHeight: { xs: 120, md: 155 } }}
          >
            {/* Left Side - Profile Picture and Price */}
            <Stack
              direction="column"
              alignItems="center"
              spacing={2}
              sx={{ 
                width: { xs: '30%', md: 'auto' },
                minWidth: { xs: 'auto', md: 120 },
              }}
            >
              <ProfilePicture src={teaching.teacher.profilePicture || undefined} />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '22px', md: '26px' },
                  lineHeight: 1.2,
                }}
              >
                {teaching.price} €/h
              </Typography>
            </Stack>

            {/* Main Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Name and Tags */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '16px', md: '19px' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    mr: 2,
                  }}
                >
                  {teaching.teacher.username}
                </Typography>
                
                <Stack direction="row" spacing={1} alignItems="center">
                  {teaching.acceptsRemote && (
                    <Tooltip tooltipId="common.tooltip.acccepts-remote.hint">
                      <Chip
                        icon={
                          <Box
                            component="img"
                            src={accepts_remote_icon}
                            sx={{ width: 20, height: 20 }}
                          />
                        }
                        label="Remote"
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          '& .MuiChip-icon': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                  <AvgResponseTimeComponent
                    avgResponseTime={teaching.teacher.avgResponseTime}
                  />
                </Stack>
              </Stack>

              {/* Description */}
              <Box sx={{ flex: 1, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    fontWeight: { xs: 600, md: 500 },
                    fontSize: { xs: '13px', md: '14px' },
                    lineHeight: { xs: 1.4, md: 1.5 },
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                  }}
                >
                  {teaching.description}
                </Typography>
              </Box>

              {/* Thumbs Section */}
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                sx={{ mt: 'auto' }}
                role="group"
                aria-label="Teacher rating"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton 
                    size="small" 
                    sx={{ p: 0.5 }}
                    aria-label={`${teaching.likeCount} likes`}
                    tabIndex={0}
                  >
                    <ThumbUp sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </IconButton>
                  <Typography 
                    variant="caption" 
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                    aria-label={`${teaching.likeCount} likes`}
                  >
                    {teaching.likeCount}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton 
                    size="small" 
                    sx={{ p: 0.5 }}
                    aria-label={`${teaching.dislikeCount} dislikes`}
                    tabIndex={0}
                  >
                    <ThumbDown sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </IconButton>
                  <Typography 
                    variant="caption" 
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                    aria-label={`${teaching.dislikeCount} dislikes`}
                  >
                    {teaching.dislikeCount}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      
      {!hideLine && (
        <Divider 
          sx={{ 
            display: { xs: 'block', md: 'none' },
            borderColor: '#acacac',
            mt: 2,
          }} 
        />
      )}
    </Box>
  );
});

TeacherSmallComponent.displayName = 'TeacherSmallComponent';

